export type IntegrationMetaData = { [key: string]: string };

export interface IntegrationAccess {
    id: string;
    provider: string;
    metaData: IntegrationMetaData;
}

export interface FormDataKeyValuePair {
    key: string;
    value: string;
}

export function isAccessRefreshToken(tokens: AccessToken | AccessRefreshToken): tokens is AccessRefreshToken {
    return tokens.hasOwnProperty('refresh_token');
}

export interface AccessToken {
    access_token: string;
}

export interface AccessRefreshToken {
    access_token: string;
    refresh_token: string;
}

export interface GlueIntegration<MetaData extends {}> {
    provider: string;
    allowedDomains: string[];
    renderOptionLabel: (metaData: MetaData) => string;
    getProviderAccountIdFromMetaData?: (metaData: MetaData) => string;
    integrationType: string;
}

export interface OAuthIntegration<
    TokenType extends AccessToken | AccessRefreshToken,
    RequiredSecrets extends { [key: string]: string },
    MetaData extends {},
> extends GlueIntegration<MetaData> {
    integrationType: 'oauth';
    requiredSecrets: (keyof RequiredSecrets)[];
    getAuthorizationUrl: (oauthState: string, redirectUri: string, secrets: RequiredSecrets) => string;
    getAccessTokenUrl: (secrets: RequiredSecrets) => string;
    getGrantTypeTokenFormData: (
        queryParams: { [key: string]: string },
        redirectUri: string,
        secrets: RequiredSecrets,
    ) => FormDataKeyValuePair[];
    getRefreshTokenFormData?: (refreshToken: string, secrets: RequiredSecrets) => FormDataKeyValuePair[];
    resolveTokenAndMetaDataFromSuccessResponse: (responseData: any) => {
        tokens: TokenType;
        providerAccountId?: string;
        metaData?: MetaData;
    };
}

export function isOAuthIntegration(integration: GlueIntegration<any>): integration is OAuthIntegration<any, any, any> {
    return integration.integrationType === 'oauth';
}

export interface OktaIntegrationType
    extends GlueIntegration<{
        oktaDomain: string;
    }> {
    provider: 'okta';
    integrationType: 'okta';
}

export function isOktaIntegration(integration: GlueIntegration<any>): integration is OktaIntegrationType {
    return integration.provider === 'okta' && integration.integrationType === 'okta';
}
